import { useTranslation } from 'react-i18next'
import { Check, X, Search, AlertTriangle } from 'lucide-react'
import type { AuditReport } from '@/lib/api/amazon-listing'
import { SectionCard } from './shared/SectionCard'
import { RecommendationCards } from './shared/RecommendationCards'

interface KeywordMatrixProps {
  matrix: AuditReport['keyword_matrix']
}

const PLACEMENT_LABELS: Record<string, string> = {
  title: 'Titel',
  bullets: 'Bullets',
  description: 'Beschreibung',
  backend_keywords: 'Backend-Keywords',
}

function translatePlacement(raw: string): string {
  return raw
    .split(',')
    .map((p) => PLACEMENT_LABELS[p.trim()] ?? p.trim())
    .join(', ')
}

export function KeywordMatrix({ matrix }: KeywordMatrixProps) {
  const { t } = useTranslation()
  if (!matrix) return null
  const r = (key: string) => t(`amazonListing.report.${key}`)
  const cov = matrix.coverage

  return (
    <div className="space-y-4">
      <SectionCard title={r('keywordMatrix')} score={matrix.score}>
        <div className="flex items-center gap-2 mb-4">
          <Search className="w-4 h-4 text-neutral-500" />
          <span className="text-2xl font-bold text-neutral-900 dark:text-white">{cov?.coverage_percent ?? 0}%</span>
          <span className="text-xs text-neutral-500">{r('coveragePercent')}</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs text-neutral-500 border-b border-neutral-200 dark:border-neutral-700">
                <th className="text-left py-2 pr-3">{r('keyword')}</th>
                <th className="text-center py-2 px-1">{r('origin')}</th>
                <th className="text-center py-2 px-2">{r('title')}</th>
                <th className="text-center py-2 px-2">{r('bullets')}</th>
                <th className="text-center py-2 px-2">{r('description')}</th>
                <th className="text-left py-2 px-2">{r('competitors')}</th>
                <th className="text-left py-2 pl-2">{r('signal')}</th>
              </tr>
            </thead>
            <tbody>
              {matrix.matrix?.map((row, i) => (
                <tr key={i} className={`border-b border-neutral-100 dark:border-neutral-700 ${row.origin === 'competitor_only' ? 'bg-orange-50/50 dark:bg-orange-900/5' : ''}`}>
                  <td className="py-2 pr-3 font-medium text-neutral-900 dark:text-white">{row.keyword}</td>
                  <td className="py-2 px-1 text-center"><OriginBadge origin={row.origin} r={r} /></td>
                  <td className="py-2 px-2 text-center"><CheckIcon ok={row.source?.in_title} /></td>
                  <td className="py-2 px-2 text-center"><CheckIcon ok={row.source?.in_bullets} /></td>
                  <td className="py-2 px-2 text-center"><CheckIcon ok={row.source?.in_description} /></td>
                  <td className="py-2 px-2">
                    <div className="flex items-center gap-2">
                      <div className="w-16 h-2 bg-neutral-100 dark:bg-neutral-700 rounded-full overflow-hidden">
                        <div className="h-full bg-orange-500 rounded-full" style={{ width: `${(row.competitor_frequency ?? 0) * 100}%` }} />
                      </div>
                      <span className="text-xs text-neutral-500">{Math.round((row.competitor_frequency ?? 0) * 100)}%</span>
                    </div>
                  </td>
                  <td className="py-2 pl-2"><SignalBadge strength={row.signal_strength} r={r} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </SectionCard>

      {matrix.gaps?.length > 0 && (
        <div className="bg-white dark:bg-neutral-800 rounded-xl border border-neutral-200 dark:border-neutral-700 p-4">
          <h4 className="flex items-center gap-2 text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-3">
            <AlertTriangle className="w-4 h-4 text-red-500" /> {r('keywordGaps')}
          </h4>
          <div className="space-y-2">
            {matrix.gaps.map((gap, i) => (
              <div key={i} className="flex flex-wrap items-center justify-between gap-1 bg-red-50/50 dark:bg-red-900/10 rounded-lg px-3 py-2">
                <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 min-w-0">
                  <span className="text-sm font-medium text-neutral-900 dark:text-white">{gap.keyword}</span>
                  <span className="text-xs text-neutral-500">{Math.round(gap.competitor_frequency * 100)}% {r('competitors')}</span>
                </div>
                <span className="px-2 py-0.5 rounded-full text-xs bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400 whitespace-nowrap">
                  → {translatePlacement(gap.suggested_placement)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {matrix.recommendations?.length > 0 && <RecommendationCards recs={matrix.recommendations} t={t} />}
    </div>
  )
}

function CheckIcon({ ok }: { ok?: boolean }) {
  return ok ? <Check className="w-4 h-4 text-green-500 mx-auto" /> : <X className="w-4 h-4 text-neutral-300 mx-auto" />
}

function SignalBadge({ strength, r }: { strength: string; r: (k: string) => string }) {
  const level = strength || 'low'
  const colors: Record<string, string> = {
    high: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
    medium: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
    low: 'bg-neutral-100 text-neutral-600 dark:bg-neutral-700 dark:text-neutral-400',
  }
  return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${colors[level] || colors.low}`}>{r(`level.${level}`)}</span>
}

function OriginBadge({ origin, r }: { origin?: string; r: (k: string) => string }) {
  const cfg: Record<string, { cls: string; label: string }> = {
    shared: { cls: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400', label: r('originShared') },
    source_only: { cls: 'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400', label: r('originSourceOnly') },
    competitor_only: { cls: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400', label: r('originCompetitorOnly') },
  }
  const c = cfg[origin || 'shared'] || cfg.shared
  return <span className={`px-1.5 py-0.5 rounded text-2xs font-medium whitespace-nowrap ${c.cls}`}>{c.label}</span>
}
